import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { toast } from '../../toasts';
import { useNavigate, useParams } from 'react-router-dom';
import DebtService from '../../services/debt.service';
import { getError, showCurrency } from '../../utils';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function ContractDebts({ contract, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const { id } = useParams();
	const [deletingDebt, setDeletingDebt] = useState(null);

	const isClosed = contract?.status === 'closed';

	const stats = useMemo(() => {
		const balanceByCurrency = new Map();
		const amountByCurrency = new Map();
		let nextDue = null;
		rows.forEach((debt) => {
			const currency = debt.currency || '—';
			balanceByCurrency.set(currency, (balanceByCurrency.get(currency) || 0) + (debt.balance || 0));
			amountByCurrency.set(currency, (amountByCurrency.get(currency) || 0) + (debt.amount || 0));
			if (debt.endDate && (!nextDue || moment.utc(debt.endDate).isBefore(moment.utc(nextDue.endDate)))) {
				nextDue = debt;
			}
		});
		return [
			{ label: t('entity.debt.plural'), value: rows.length },
			{
				label: t('field.dueAmount'),
				value: [...amountByCurrency.entries()].map(([currency, amount]) => formatMoney(currency, amount)).join(' · ') || '—',
				mono: true,
			},
			{
				label: t('field.previousBalance'),
				value: [...balanceByCurrency.entries()].map(([currency, amount]) => formatMoney(currency, amount)).join(' · ') || '—',
				mono: true,
				subTone: [...balanceByCurrency.values()].some((amount) => amount > 0) ? 'down' : 'neutral',
			},
			{
				label: t('field.endDate'),
				value: nextDue ? formatDate(nextDue.endDate) : '—',
				small: true,
				mono: true,
				sub: nextDue ? formatMoney(nextDue.currency, nextDue.amount) : undefined,
			},
		];
	}, [rows, t]);

	function newDebtHandler() {
		navigate(`/AdminScreen/contracts/contract/${id}/newDebt`);
	}

	function editDebtHandler(debt) {
		navigate(`/AdminScreen/contracts/contract/${id}/debts/${debt._id}/edit`);
	}

	async function handleConfirmDelete() {
		try {
			await DebtService.delete(deletingDebt._id);
			toast.success(t('msg.debtDeleted'));
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingDebt(null);
		}
	}

	const columns = [
		{ key: 'id', header: 'ID', render: (debt) => <IdChip>{debt.customId}</IdChip> },
		{ key: 'concept', header: t('entity.concept'), render: (debt) => debt.concept },
		{ key: 'date', header: t('field.date'), render: (debt) => formatDate(debt.date) },
		{ key: 'endDate', header: t('field.endDate'), render: (debt) => formatDate(debt.endDate) },
		{
			key: 'amount',
			header: t('field.dueAmount'),
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.amount)}</span>,
		},
		{
			key: 'balance',
			header: t('field.balance'),
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.balance)}</span>,
		},
	];

	const renderRowActions = (debt) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => editDebtHandler(debt) },
				{ label: t('action.delete'), onClick: () => setDeletingDebt(debt), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			{!isClosed && (
				<div className='tw:flex tw:justify-end'>
					<Button size='sm' variant='primary' onClick={newDebtHandler}>
						{t('action.addCollection')}
					</Button>
				</div>
			)}
			<Section title={t('title.collections')}>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(debt) => navigate(`/AdminScreen/debts/debt/${debt._id}`)}
					rowActions={renderRowActions}
					emptyMessage={t('msg.noPendingDuesForContract')}
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingDebt)}
				handleClose={() => setDeletingDebt(null)}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteDebt')}
				description={t('msg.confirmDeleteDebtShort')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default ContractDebts;
